import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import ListFilters from "@/common/components/ListFilters";
import ListHeader from "@/common/components/ListHeader";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import { cardTypeOptions } from "@/common/constants/cards.constant";
import useDebounce from "@/common/hooks/useDebounce";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { Mapper } from "@/common/services/mapper";
import { Option } from "@/common/types/option";
import CardsTotalizerResume from "@/features/cards/components/allCards/CardsTotalizerResume";
import { allCardsDefaultColumns } from "@/features/cards/constants/allCardsDefaultColumns.constant";
import { useCards } from "@/features/cards/hooks/useCards";
import { useCardsTotalizer } from "@/features/cards/hooks/useCardsTotalizer";
import { AllCardsTransformer } from "@/features/cards/services/allCardsTransformer";
import { CardsTotalizerData } from "@/features/cards/types/cardsTotalizer.type";
import { useCompanies } from "@/features/companies/hooks/useCompanies";
import { CompaniesTransformer } from "@/features/companies/services/companiesTransformer";
import { Search, WalletCardsIcon } from "lucide-react";
import { cardStatusTranslation } from "../../../constants/cardStatusTranslation.constant";

const AllCardsList = () => {
  const { filterParams, setFilterParams } = useFilterParams();

  const [search, setSearch] = useDebounce(filterParams.search || '');
  const [searchCompany, setSearchCompany] = useDebounce('');

  const { data: companiesOptions } = useCompanies({
    filters: { search: searchCompany },
    mapper: Mapper.mapWithTransformer({ transformer: CompaniesTransformer.optionsTransformer() }),
  });

  const {
    data: cards,
    meta,
    isPending: cardsIsPending,
    error: cardsError = {} as any,
    refetch: refetchCards,
  } = useCards({
    filters: {
      type: filterParams.type?.value,
      company_id: filterParams.company?.value,
      search,
      status: filterParams.status?.value,
      page: filterParams.page || 1,
    },
    mapper: Mapper.mapWithTransformer({ transformer: AllCardsTransformer.tableTransformer() })
  });

  const { data: cardsTotalizer } = useCardsTotalizer<CardsTotalizerData>({
    filters: {
      type: filterParams.type?.value,
      company_id: filterParams.company?.value,

    },
  });

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        Icon={<WalletCardsIcon className="w-10 h-10" strokeWidth={1} />}
        title="Todos os cartões"
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-200px)]"
        statusCode={cardsError?.status}
        refetchFunction={refetchCards}
      >
        <CardsTotalizerResume cardsTotalizer={cardsTotalizer} />

        <div className="h-[calc(100vh-200px)] flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              LeftIcon={<Search />}
              placeholder="Número do cartão"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onChange={(e) => {
                setSearch((e.target as HTMLInputElement).value);
                setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }));
              }}
              value={filterParams.search}
            />

            <ListFilters
              filters={[
                {
                  className: "w-30",
                  onChange: (status) => setFilterParams((prev) => ({ ...prev, status, page: 1 })),
                  value: filterParams.status,
                  options: [{ label: "Todas", value: "" }, ...Object.entries(cardStatusTranslation).map(([value, label]) => ({ value, label }))],
                  placeholder: "Status",
                  dataTestid: 'all_cards_status_filter'
                },
                {
                  className: '',
                  onChange: (e) => setFilterParams((prev) => ({ ...prev, type: e, page: 1 })),
                  value: filterParams.type,
                  options: [{ label: 'Todas', value: '' }, ...cardTypeOptions],
                  placeholder: 'Tipo'
                },
                {
                  className: '',
                  onChange: (e) => setFilterParams((prev) => ({ ...prev, company: e, page: 1 })),
                  onInputChange: setSearchCompany,
                  value: filterParams.company,
                  options: [{ label: 'Todas', value: '' }, ...companiesOptions as Option[]],
                  placeholder: 'Empresa'
                },
              ]}
            />
          </div>

          <Table
            columns={allCardsDefaultColumns}
            isLoadingData={cardsIsPending}
            page={filterParams.page || 1}
            rows={cards}
            totalPages={meta.total_pages}
            totalCount={meta.total}
            hasPagination
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
            rowEmptyValue="--"
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  );
};

export default AllCardsList;
